import React, { useMemo } from 'react';
import { Typography, Tag, Upload, Button, Tooltip, message } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import CrudPage from '../../../components/common/CrudPage';
import eventService from '../../../services/cms/eventServiceRequest';
import eventCategoryService from '../../../services/cms/eventCategoryServiceRequest';
import eventTypeService from '../../../services/cms/eventTypeServiceRequest';
import objectService from '../../../services/cms/objectServiceRequest';
import { TIMEZONE_OPTIONS } from '../../../constants/timezones';
import DynamicTypeColumns from './components/DynamicTypeColumns';
import LoopInfoField from './components/LoopInfoField';

const { Text } = Typography;

const STATUS_OPTIONS = [
  { label: 'Duyệt', value: 1 },
  { label: 'Đợi duyệt', value: 0 },
];

const DATE_TYPE_OPTIONS = [
  { label: 'Lịch dương', value: 0 },
  { label: 'Lịch âm', value: 1 },
];

const DATETIME_FORMAT = 'DD/MM/YYYY HH:mm:ss';
const SUBMIT_DATETIME_FORMAT = 'YYYY-MM-DD HH:mm:ss';

// select filterOption dùng chung (tìm theo label)
const searchByLabel = (input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase());

const toFileList = (url) =>
  url ? [{ uid: '-1', name: url.split('/').pop() || 'image', status: 'done', url, thumbUrl: url }] : [];

const parseColumns = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
};

const safeParseMeta = (raw) => {
  if (!raw) return {};
  if (typeof raw === 'object') return raw;
  try {
    return JSON.parse(raw) || {};
  } catch (e) {
    return {};
  }
};

const META_JSON_EXAMPLE = 'Metadata phải là một object JSON, ví dụ {"key": "value"}';

// JSON.parse chỉ báo offset ký tự — quy về dòng/cột để người dùng tìm được chỗ sai trong textarea
const describeJsonError = (raw, error) => {
  const matched = /position (\d+)/.exec(error?.message || '');
  if (!matched) return `Metadata không phải JSON hợp lệ (${error?.message || 'sai cú pháp'}). ${META_JSON_EXAMPLE}`;
  const position = Number(matched[1]);
  const before = String(raw).slice(0, position);
  const line = before.split('\n').length;
  const column = position - before.lastIndexOf('\n');
  return `Metadata sai cú pháp JSON tại dòng ${line}, cột ${column}. ${META_JSON_EXAMPLE}`;
};

// Dùng lúc LƯU: khác safeParseMeta ở chỗ KHÔNG nuốt lỗi.
// Nuốt lỗi ở đây đồng nghĩa ghi đè metadata cũ bằng "{}" mà người dùng không hề biết.
const parseMetaStrict = (raw) => {
  if (raw === undefined || raw === null || String(raw).trim() === '') return {};
  if (typeof raw === 'object') return raw;
  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch (e) {
    throw new Error(describeJsonError(raw, e));
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error(`Metadata đang là ${Array.isArray(parsed) ? 'một mảng' : 'giá trị đơn'}. ${META_JSON_EXAMPLE}`);
  }
  return parsed;
};

// Chặn ngay tại form để người dùng thấy lỗi ở đúng ô, thay vì submit rồi mới báo
const metadataJsonRule = {
  validator: (_, value) => {
    try {
      parseMetaStrict(value);
      return Promise.resolve();
    } catch (e) {
      return Promise.reject(new Error(e.message));
    }
  },
};

const QuanLySuKienHeThong = () => {
  const queryClient = useQueryClient();

  // Import sự kiện từ Excel (rule engine ở backend, giống lichviet_cms)
  const importMutation = useMutation({
    mutationFn: eventService.importExcel,
    onSuccess: (res) => {
      const imported = res?.data?.imported ?? 0;
      message.success(res?.message || `Import thành công ${imported} sự kiện`);
      queryClient.invalidateQueries({ queryKey: ['eventList'] });
    },
    onError: (error) => {
      // Interceptor axios đã tự hiện thông báo cho lỗi 400; fallback cho trường hợp khác
      const msg = error?.data?.message || error?.message;
      if (msg) message.error(msg);
    },
  });

  const handleBeforeUpload = (file) => {
    const isValid = /\.(xlsx|csv)$/i.test(file.name);
    if (!isValid) {
      message.error('Chỉ chấp nhận file .xlsx hoặc .csv (file .xls hãy lưu lại thành .xlsx)');
      return Upload.LIST_IGNORE;
    }
    importMutation.mutate(file);
    return false; // chặn upload mặc định của antd
  };

  const headerActions = (
    <Tooltip title="File Excel: cột A = STT, cột B = tên rule (nnnx / drukani / drukanivi / druk / drukvi), từ cột C là dữ liệu. Dòng 1 là tiêu đề.">
      <Upload accept=".xlsx,.csv" showUploadList={false} beforeUpload={handleBeforeUpload} maxCount={1}>
        <Button icon={<UploadOutlined />} loading={importMutation.isPending}>
          Import Excel
        </Button>
      </Upload>
    </Tooltip>
  );

  // Danh mục sự kiện
  const { data: categoriesData } = useQuery({
    queryKey: ['allEventCategories'],
    queryFn: () => eventCategoryService.getList({ page: 1, limit: 1000 }),
  });

  // Thể loại sự kiện (kèm cột columns để render trường động)
  const { data: typesData } = useQuery({
    queryKey: ['allEventTypes'],
    queryFn: () => eventTypeService.getList({ page: 1, limit: 1000 }),
  });

  // Đối tượng (dùng cho Địa điểm + Đối tượng)
  const { data: objectsData } = useQuery({
    queryKey: ['allObjects'],
    queryFn: () => objectService.getAll({ page: 1, limit: 1000 }),
  });

  const categoriesList = useMemo(() => categoriesData?.data?.data || categoriesData?.data || [], [categoriesData]);
  const typesList = useMemo(() => typesData?.data?.data || typesData?.data || [], [typesData]);
  const objectsList = useMemo(() => objectsData?.data?.data || objectsData?.data || [], [objectsData]);

  const categoryOptions = useMemo(
    () => categoriesList.map((item) => ({ label: `[${item.id}] ${item.title || item.name}`, value: item.id })),
    [categoriesList]
  );

  const typeOptions = useMemo(
    () => typesList.map((item) => ({ label: `[${item.id}] ${item.name}`, value: item.id })),
    [typesList]
  );

  // Địa điểm: chỉ các đối tượng is_place = 1
  const locationOptions = useMemo(
    () =>
      objectsList
        .filter((o) => Number(o.is_place) === 1)
        .map((o) => ({ label: o.title, value: o.id })),
    [objectsList]
  );

  // Đối tượng: tất cả object
  const objectOptions = useMemo(
    () => objectsList.map((o) => ({ label: o.title, value: o.id })),
    [objectsList]
  );

  const filterFields = useMemo(
    () => [
      { label: 'Danh mục', value: 'category_id', options: [{ label: 'Tất cả danh mục', value: '' }, ...categoryOptions] },
      { label: 'Thể loại', value: 'type_id', options: [{ label: 'Tất cả thể loại', value: '' }, ...typeOptions] },
      {
        label: 'Trạng thái',
        value: 'status',
        options: [
          { label: 'Tất cả trạng thái', value: '' },
          { label: 'Duyệt', value: '1' },
          { label: 'Đợi duyệt', value: '0' },
        ],
      },
      { label: 'Tác giả', value: 'author', type: 'input' },
      { label: 'Từ ngày', value: 'start_from', type: 'date' },
      // Giống CMS cũ: cả Từ ngày/Đến ngày đều lọc theo ngày bắt đầu sự kiện (start_time)
      { label: 'Đến ngày', value: 'start_to', type: 'date' },
      // Giống CMS cũ: lọc theo ngày/tháng (không năm), placeholder để nguyên "By Date"
      { label: 'By Date', value: 'by_date', type: 'date', format: 'DD/MM' },
    ],
    [categoryOptions, typeOptions]
  );

  const formFields = useMemo(
    () => [
      { type: 'divider', label: 'Thông tin cơ bản' },
      {
        name: 'title',
        label: 'Tiêu đề',
        type: 'input',
        span: 24,
        rules: [{ required: true, message: 'Vui lòng nhập tiêu đề!' }],
        placeholder: 'Tiêu đề sự kiện',
      },
      {
        name: 'category_id',
        label: 'Danh mục',
        type: 'select',
        span: 24,
        md: 12,
        options: categoryOptions,
        placeholder: 'Chọn danh mục',
        propsInput: { showSearch: true, allowClear: true, filterOption: searchByLabel },
      },
      {
        name: 'timezone',
        label: 'Time zone',
        type: 'select',
        span: 24,
        md: 12,
        options: TIMEZONE_OPTIONS,
        placeholder: 'Chọn time zone',
        propsInput: { showSearch: true, allowClear: true, filterOption: searchByLabel, virtual: true },
      },
      {
        name: 'type_id',
        label: 'Thể loại',
        type: 'select',
        span: 24,
        md: 12,
        options: typeOptions,
        placeholder: 'Chọn thể loại',
        propsInput: { showSearch: true, allowClear: true, filterOption: searchByLabel },
      },
      {
        name: 'loop_info',
        label: 'Kiểu lặp',
        span: 24,
        md: 12,
        // Ô nhập RRULE kèm nút mở bộ tạo chuỗi lặp (modal ngay trong CMS4)
        render: ({ value, onChange }) => <LoopInfoField value={value} onChange={onChange} />,
      },
      // Trường động theo Thể loại (lưu vào metadata)
      { type: 'html', span: 24, content: <DynamicTypeColumns typesList={typesList} /> },

      { type: 'divider', label: 'Thời gian' },
      {
        name: 'start_time',
        label: 'Thời gian bắt đầu',
        type: 'date',
        span: 24,
        md: 12,
        format: DATETIME_FORMAT,
        submitFormat: SUBMIT_DATETIME_FORMAT,
        propsInput: { showTime: { format: 'HH:mm:ss' }, format: DATETIME_FORMAT },
      },
      {
        name: 'end_time',
        label: 'Thời gian kết thúc',
        type: 'date',
        span: 24,
        md: 12,
        format: DATETIME_FORMAT,
        submitFormat: SUBMIT_DATETIME_FORMAT,
        propsInput: { showTime: { format: 'HH:mm:ss' }, format: DATETIME_FORMAT },
      },
      {
        name: 'date_type',
        label: 'Kiểu ngày tháng',
        type: 'radio',
        span: 24,
        md: 12,
        typeRadioType: 'horizontal',
        options: DATE_TYPE_OPTIONS,
        defaultValue: 0,
      },
      {
        name: 'allday',
        label: 'Cả ngày',
        type: 'checkbox',
        span: 24,
        md: 12,
        labelCheckbox: 'All Day',
        defaultChecked: false,
      },

      // Giống CMS cũ: mỗi ảnh vừa upload được vừa dán được link có sẵn
      { type: 'divider', label: 'Hình ảnh' },
      { name: 'image_url', label: 'Ảnh đại diện', type: 'upload-cdn', span: 24, md: 12, uploadFolder: 'events', enterLink: true, linkPlaceholder: 'Link ảnh đại diện' },
      { name: 'home_background', label: 'Ảnh home background', type: 'upload-cdn', span: 24, md: 12, uploadFolder: 'events', enterLink: true, linkPlaceholder: 'Link ảnh home background' },
      { name: 'background', label: 'Ảnh background', type: 'upload-cdn', span: 24, md: 12, uploadFolder: 'events', enterLink: true, linkPlaceholder: 'Link ảnh background' },
      { name: 'icon', label: 'Ảnh Icon', type: 'upload-cdn', span: 24, md: 12, uploadFolder: 'events', enterLink: true, linkPlaceholder: 'Link ảnh Icon' },

      { type: 'divider', label: 'Địa điểm & Đối tượng' },
      {
        name: 'location',
        label: 'Địa điểm',
        type: 'select',
        span: 24,
        md: 12,
        options: locationOptions,
        placeholder: 'Chọn địa điểm',
        propsInput: { showSearch: true, allowClear: true, filterOption: searchByLabel },
      },
      {
        name: 'tags',
        label: 'Đối tượng',
        type: 'select',
        span: 24,
        md: 12,
        options: objectOptions,
        placeholder: 'Chọn đối tượng',
        propsInput: { mode: 'multiple', showSearch: true, allowClear: true, filterOption: searchByLabel },
      },
      { name: 'alert_type', label: 'Alert Info', type: 'input', span: 24, md: 12, placeholder: 'Alert Info' },

      { type: 'divider', label: 'Nội dung & Nâng cao' },
      { name: 'content', label: 'Nội dung', type: 'ckeditor', span: 24 },
      {
        name: 'metadata',
        label: 'Metadata (JSON)',
        type: 'textarea',
        span: 24,
        placeholder: '{"key": "value"}',
        propsInput: { rows: 3 },
        rules: [metadataJsonRule],
      },
      { name: 'status', label: 'Trạng thái', type: 'select', span: 24, md: 12, options: STATUS_OPTIONS, defaultValue: 1 },
    ],
    [categoryOptions, typeOptions, typesList, locationOptions, objectOptions]
  );

  // record (DB) -> form
  const mapDataToForm = (record) => {
    const meta = safeParseMeta(record.metadata);
    const result = {};

    // Trường động theo thể loại -> chỉ nạp các cột của type hiện tại
    const type = typesList.find((t) => String(t.id) === String(record.type_id));
    const cols = parseColumns(type?.columns);
    if (cols.length) {
      const metaCols = {};
      cols.forEach((c) => {
        metaCols[c] = meta[c] ?? '';
      });
      result.metaCols = metaCols;
    }

    // background / icon lưu trong metadata (parity CMS cũ: Background / Icon_new)
    result.background = toFileList(meta.Background);
    result.icon = toFileList(meta.Icon_new);

    // Đối tượng: chuỗi ",1,2," -> [1,2]
    if (record.tags) {
      result.tags = String(record.tags)
        .split(',')
        .map((v) => v.trim())
        .filter(Boolean)
        .map((v) => Number(v));
    } else {
      result.tags = [];
    }

    // Địa điểm: id (chuỗi) -> number
    result.location = record.location ? Number(record.location) : undefined;

    // allday -> boolean cho checkbox
    result.allday = Number(record.allday) === 1;

    return result;
  };

  // form -> payload gửi API
  const mapFormToData = (payload) => {
    // 1. metadata gốc từ textarea — parse lỗi thì dừng hẳn, KHÔNG ghi đè bằng {}
    const meta = parseMetaStrict(payload.metadata);

    // 2. trường động theo thể loại.
    // Bỏ qua key có value undefined: ô chỉ được mount mà chưa từng nạp giá trị
    // (danh sách thể loại về sau khi modal đã mở) sẽ không xóa trắng key có sẵn trong metadata.
    // Ô bị người dùng xóa trắng trả về '' nên vẫn clear được như CMS cũ.
    if (payload.metaCols && typeof payload.metaCols === 'object') {
      Object.entries(payload.metaCols).forEach(([k, v]) => {
        if (v !== undefined) meta[k] = v;
      });
    }

    // 3. background / icon -> metadata (parity CMS cũ)
    const bgUrl = Array.isArray(payload.background) ? payload.background[0]?.url : payload.background;
    const iconUrl = Array.isArray(payload.icon) ? payload.icon[0]?.url : payload.icon;
    if (bgUrl) meta.Background = bgUrl;
    else delete meta.Background;
    if (iconUrl) meta.Icon_new = iconUrl;
    else delete meta.Icon_new;

    // 4. Đối tượng: [1,2] -> ",1,2," (giống CMS cũ)
    let tags = '';
    if (Array.isArray(payload.tags) && payload.tags.length) {
      tags = ',' + payload.tags.join(',') + ',';
    }

    return {
      metadata: JSON.stringify(meta),
      tags,
      allday: payload.allday ? 1 : 0,
      date_type:
        payload.date_type === undefined || payload.date_type === null || payload.date_type === ''
          ? undefined
          : Number(payload.date_type),
      location: payload.location != null && payload.location !== '' ? String(payload.location) : '',
      alert_type: payload.alert_type ?? '',
      // Loại bỏ các field chỉ dùng ở FE (API không nhận -> tránh lỗi validate unknown key)
      metaCols: undefined,
      background: undefined,
      icon: undefined,
    };
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    { title: 'TIÊU ĐỀ', dataIndex: 'title', key: 'title', render: (text) => <Text strong>{text}</Text> },
    {
      title: 'ẢNH',
      dataIndex: 'image_url',
      key: 'image_url',
      width: 80,
      render: (url) =>
        url ? <img src={url} alt="" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }} /> : '-',
    },
    { title: 'DANH MỤC', dataIndex: 'category_id', key: 'category_id', width: 150, render: (val, r) => (r.category_name ? `[${val}] ${r.category_name}` : val || '-') },
    { title: 'THỂ LOẠI', dataIndex: 'type_id', key: 'type_id', width: 150, render: (val, r) => (r.type_name ? `[${val}] ${r.type_name}` : val || '-') },
    { title: 'NGƯỜI TẠO', dataIndex: 'author_name', key: 'author_name', width: 140, render: (val, r) => val || r.create_by || r.created_by || '-' },
    { title: 'NGÀY BẮT ĐẦU', dataIndex: 'start_time', key: 'start_time', width: 150, render: (t) => (t ? (dayjs(t).isValid() ? dayjs(t).format('DD/MM/YYYY HH:mm') : t) : '-') },
    { title: 'TRẠNG THÁI', dataIndex: 'status', key: 'status', width: 110, render: (status) => <Tag color={status === 1 ? 'green' : 'orange'}>{status === 1 ? 'DUYỆT' : 'ĐỢI DUYỆT'}</Tag> },
    { title: 'NGÀY TẠO', dataIndex: 'create_time', key: 'create_time', width: 140, render: (time) => (time ? dayjs(time).format('DD/MM/YYYY HH:mm') : '-') },
  ];

  return (
    <CrudPage
      title="QUẢN LÝ SỰ KIỆN HỆ THỐNG"
      subTitle="Quản lý các sự kiện hệ thống (admin)"
      itemsName="Sự kiện"
      queryKey="eventList"
      service={eventService}
      columns={columns}
      formFields={formFields}
      filterFields={filterFields}
      searchPlaceholder="Tìm kiếm theo tiêu đề..."
      addButtonLabel="Đăng sự kiện"
      formConfig={{ width: 1000 }}
      renderHeaderActions={headerActions}
      mapDataToForm={mapDataToForm}
      mapFormToData={mapFormToData}
    />
  );
};

export default QuanLySuKienHeThong;
